import { Box, Divider, Stack, useTheme } from "@mui/material";
import { PrimaryPaper } from "Components/Shared/PrimaryPaper";
import { Resources, useResource } from "Translations/Resources";
import { CampaignImageType } from "Infrastructure/Api/Api";
import { FormColorInput } from "Components/Shared/Inputs/Form/FormColorInput";
import { CampaignContentSection } from "./CampaignContentSection";
import { CampaignImageUpload } from "./CampaignImageUpload";
import { PreviewSection } from "./Preview/PreviewSection";
import FavoriteIcon from "@mui/icons-material/Favorite";
import type { Control, UseFormWatch } from "react-hook-form";
import type { CampaignFormData } from "./campaignFormSchema";

const RESOURCES = Resources.Admin.Campaigns.Form;

type Props = {
  formId: string;
  onSubmit: React.FormEventHandler;
  control: Control<CampaignFormData>;
  watch: UseFormWatch<CampaignFormData>;
  isSubmitting: boolean;
  campaignId: number;
};

export const LandingPageStep = ({
  formId,
  onSubmit,
  control,
  watch,
  isSubmitting,
  campaignId,
}: Props) => {
  const { t } = useResource();
  const theme = useTheme();

  return (
    <PrimaryPaper>
      <Box
        display="grid"
        gridTemplateColumns={["1fr", "auto auto 1fr"]}
        gap={0}
        alignItems="start"
      >
        <Box
          id={formId}
          component="form"
          onSubmit={onSubmit}
          sx={{ minWidth: 230 }}
        >
          <Stack gap={4}>
            <CampaignImageUpload
              campaignId={campaignId}
              imageType={CampaignImageType.Logo}
              label={t(RESOURCES.Logo)}
            />
            <CampaignImageUpload
              campaignId={campaignId}
              imageType={CampaignImageType.Icon}
              label={t(RESOURCES.Icon)}
              placeholder={
                <FavoriteIcon sx={{ fontSize: 32, color: "text.disabled" }} />
              }
            />
            <CampaignImageUpload
              campaignId={campaignId}
              imageType={CampaignImageType.Landing}
              label={t(RESOURCES.LandingImage)}
              placeholder={
                <Box sx={{ width: 1, height: 1, bgcolor: "grey.300" }} />
              }
            />
            <FormColorInput
              control={control}
              name="color"
              label={t(RESOURCES.Color)}
              helperText={t(RESOURCES.ColorHelp)}
              disabled={isSubmitting}
              placeholderColor={theme.palette.primary.main}
            />
            <CampaignContentSection
              control={control}
              watch={watch}
              isSubmitting={isSubmitting}
            />
          </Stack>
        </Box>
        <Divider orientation="vertical" flexItem sx={{ mx: 7 }} />
        <PreviewSection
          campaignId={campaignId}
          variant="campaign-page"
          formValues={watch()}
        />
      </Box>
    </PrimaryPaper>
  );
};
